import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  ClipboardList,
  Eye,
  Pencil,
  Plus,
  Save,
  Search,
} from "lucide-react";

import { Button, Drawer } from "@/design-system/primitives";
import { HealthieFormRenderer } from "@/features/onboarding/HealthieFormRenderer";
import {
  useCreateClinicianIntakeForm,
  useCreateClinicianIntakeModule,
  useClinicianIntakeForms,
  useClinicianProducts,
  useUpdateClinicianIntakeHighlight,
  useUpdateClinicianIntakeModule,
  useUpdateClinicianIntakeProductLinks,
} from "@/lib/api/clinicianHooks";
import { ApiError } from "@/lib/api/client";
import type {
  ClinicianIntakeTemplate,
  ClinicianProduct,
  IntakeTemplateModule,
} from "@/types/clinician";

const QUESTION_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "radio", label: "Single choice" },
  { value: "checkbox", label: "Multiple choice" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
];

export function IntakeFormsScreen() {
  const forms = useClinicianIntakeForms();
  const products = useClinicianProducts();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return (forms.data ?? []).filter(
      (form) =>
        !query ||
        form.name.toLocaleLowerCase().includes(query) ||
        form.linked_products.some(
          (product) =>
            product.label.toLocaleLowerCase().includes(query) ||
            product.product_id.toLocaleLowerCase().includes(query),
        ),
    );
  }, [forms.data, search]);
  const linkConflicts = useMemo(
    () =>
      (forms.data ?? []).flatMap((form) =>
        form.linked_products.flatMap((product) =>
          (product.conflicting_usages ?? []).map((usage) => ({
            formId: form.id,
            formName: form.name,
            productId: product.product_id,
            productLabel: product.label,
            usage,
          })),
        ),
      ),
    [forms.data],
  );

  useEffect(() => {
    if (!selectedId && filtered[0]) setSelectedId(filtered[0].id);
    if (selectedId && !filtered.some((form) => form.id === selectedId)) {
      setSelectedId(filtered[0]?.id ?? null);
    }
  }, [filtered, selectedId]);

  const selected = filtered.find((form) => form.id === selectedId) ?? null;

  return (
    <div className="min-h-screen px-14 md:px-24 xl:px-30 py-24 flex flex-col gap-18">
      <header className="flex flex-wrap items-end justify-between gap-12">
        <div className="flex flex-col gap-5">
          <span className="type-label-sm text-ink-muted">
            TEMPLATES · INTAKES
          </span>
          <h1 className="type-heading-xl">Intake forms</h1>
          <p className="type-body-sm text-ink-muted max-w-[70rem]">
            Live Healthie form structures, joined to the products that require
            them. A saved question publishes back to the same Healthie intake
            patients complete.
          </p>
        </div>
        <div className="w-full max-w-[46rem] flex items-center gap-7">
          <label className="relative block flex-1">
            <Search
              size={14}
              className="absolute left-10 top-1/2 -translate-y-1/2 text-ink-muted"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search forms or products"
              className="w-full min-h-38 pl-34 pr-10 border border-line bg-raised type-body-sm outline-none focus:shadow-focus"
            />
          </label>
          <Button type="button" size="sm" onClick={() => setCreating(true)}>
            <Plus size={13} /> New intake
          </Button>
        </div>
      </header>

      {linkConflicts.length > 0 && (
        <div className="border border-danger/30 bg-danger-wash px-12 py-10 flex gap-8 text-danger">
          <AlertTriangle size={16} className="shrink-0 mt-2" aria-hidden />
          <div>
            <p className="type-label-sm">Intake mapping conflict</p>
            <p className="type-body-sm mt-3">
              A medication can have only one initial form and one renewal form.{" "}
              {linkConflicts.length} conflicting form link
              {linkConflicts.length === 1 ? " is" : "s are"} flagged below;
              additional same-stage links are not supported.
            </p>
          </div>
        </div>
      )}

      {forms.isLoading ? (
        <div className="h-[34rem] bg-sunken animate-pulse" />
      ) : forms.isError ? (
        <p className="border border-danger/30 bg-danger-wash p-14 type-body-sm text-danger">
          Healthie intake templates could not be loaded.
        </p>
      ) : (
        <div className="grid lg:grid-cols-[24rem_minmax(0,1fr)] gap-14 items-start">
          <nav
            className="border border-line-subtle bg-raised max-h-[calc(100vh-11rem)] overflow-y-auto"
            aria-label="Intake templates"
          >
            <div className="px-11 py-9 border-b border-line-subtle type-label-xs text-ink-muted">
              {filtered.length} product-linked forms
            </div>
            {filtered.map((form) => (
              <button
                key={form.id}
                type="button"
                onClick={() => setSelectedId(form.id)}
                className={`w-full px-11 py-10 border-b border-line-subtle text-left flex flex-col gap-4 hover:bg-sunken ${selectedId === form.id ? "bg-accent-wash" : ""}`}
              >
                <span className="type-body-sm font-medium">{form.name}</span>
                <span className="type-label-xs text-ink-muted">
                  {form.modules.length} questions ·{" "}
                  {form.linked_products.length} products
                </span>
              </button>
            ))}
          </nav>
          {selected ? (
            <IntakeEditor
              key={selected.id}
              form={selected}
              allForms={forms.data ?? []}
              products={products.data ?? []}
              onPreview={() => setPreviewId(selected.id)}
            />
          ) : (
            <div className="border border-line-subtle p-30 text-center text-ink-muted">
              No matching intake form.
            </div>
          )}
        </div>
      )}

      <Drawer
        open={Boolean(previewId)}
        onClose={() => setPreviewId(null)}
        title={`Patient preview · ${(forms.data ?? []).find((form) => form.id === previewId)?.name ?? "Intake"}`}
        size="wide"
      >
        {previewId ? (
          <HealthieFormRenderer formId={previewId} mode="preview" />
        ) : null}
      </Drawer>

      <NewIntakeDrawer open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function IntakeEditor({
  form,
  allForms,
  products,
  onPreview,
}: {
  form: ClinicianIntakeTemplate;
  allForms: ClinicianIntakeTemplate[];
  products: ClinicianProduct[];
  onPreview: () => void;
}) {
  return (
    <section className="border border-line-subtle bg-raised min-w-0">
      <div className="px-14 py-12 border-b border-line-subtle flex flex-wrap items-start justify-between gap-10">
        <div>
          <div className="flex items-center gap-7">
            <ClipboardList size={16} aria-hidden />
            <h2 className="type-heading-md">{form.name}</h2>
          </div>
          <p className="type-label-xs text-ink-muted mt-5">
            Healthie form {form.id}
            {form.updated_at
              ? ` · updated ${new Date(form.updated_at).toLocaleString()}`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-7">
          <Button type="button" size="sm" variant="ghost" onClick={onPreview}>
            <Eye size={12} /> Preview as patient
          </Button>
          <span
            className={`type-label-xs px-8 py-5 ${form.can_publish ? "bg-success-wash text-success" : "bg-warning-wash text-warning"}`}
          >
            {form.can_publish
              ? "Healthie publishing enabled"
              : "Live mirror · read only here"}
          </span>
        </div>
      </div>

      {!form.can_publish && (
        <div className="mx-12 mt-12 border border-warning/30 bg-warning-wash px-11 py-9 flex gap-7 type-body-sm text-warning">
          <AlertTriangle size={14} className="shrink-0 mt-2" aria-hidden />
          Staging shares the production Healthie organization, so this live form
          is intentionally read-only here. Editing is enabled in the production
          clinician portal; staging-owned test duplicates remain editable.
        </div>
      )}

      <ProductLinkEditor form={form} allForms={allForms} products={products} />

      <div className="p-12 flex flex-col gap-10">
        {form.modules.map((module, index) => (
          <ModuleEditor
            key={module.id}
            formId={form.id}
            module={module}
            index={index}
            canPublish={form.can_publish}
          />
        ))}
        {form.can_publish && (
          <NewModuleForm formId={form.id} index={form.modules.length} />
        )}
      </div>
    </section>
  );
}

type IntakeUsage = "Initial" | "Renewal";

function ProductLinkEditor({
  form,
  allForms,
  products,
}: {
  form: ClinicianIntakeTemplate;
  allForms: ClinicianIntakeTemplate[];
  products: ClinicianProduct[];
}) {
  const update = useUpdateClinicianIntakeProductLinks();
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const initialSelection = () =>
    Object.fromEntries(
      form.linked_products.map((product) => [
        product.product_id,
        {
          Initial: product.usages.includes("Initial"),
          Renewal: product.usages.includes("Renewal"),
        },
      ]),
    ) as Record<string, Record<IntakeUsage, boolean>>;
  const [selection, setSelection] = useState(initialSelection);
  const catalog = useMemo(() => {
    const byId = new Map(
      products.map((product) => [product.product_id, product]),
    );
    for (const candidateForm of allForms) {
      for (const linked of candidateForm.linked_products) {
        if (!byId.has(linked.product_id)) byId.set(linked.product_id, linked);
      }
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [allForms, products]);
  const owners = useMemo(() => {
    const result = new Map<string, ClinicianIntakeTemplate>();
    for (const candidateForm of allForms) {
      if (candidateForm.id === form.id) continue;
      for (const product of candidateForm.linked_products) {
        for (const usage of product.usages) {
          result.set(`${product.product_id}:${usage}`, candidateForm);
        }
      }
    }
    return result;
  }, [allForms, form.id]);
  const query = search.trim().toLocaleLowerCase();
  const visible = catalog.filter(
    (product) =>
      !query ||
      product.label.toLocaleLowerCase().includes(query) ||
      product.product_id.toLocaleLowerCase().includes(query),
  );

  const cancel = () => {
    setSelection(initialSelection());
    setSearch("");
    setMessage(null);
    setEditing(false);
  };
  const save = async () => {
    setMessage(null);
    const linkedProducts = Object.entries(selection)
      .map(([product_id, usages]) => ({
        product_id,
        usages: (["Initial", "Renewal"] as IntakeUsage[]).filter(
          (usage) => usages[usage],
        ),
      }))
      .filter((product) => product.usages.length > 0);
    try {
      await update.mutateAsync({ formId: form.id, linkedProducts });
      setMessage("Product gates saved");
      setEditing(false);
    } catch (error) {
      setMessage(
        error instanceof ApiError &&
          error.code === "intake_product_stage_conflict"
          ? "A medication stage was linked elsewhere while you were editing. Refresh and try again."
          : "Product gates could not be saved.",
      );
    }
  };

  return (
    <div className="p-12 border-b border-line-subtle flex flex-col gap-9">
      <div className="flex items-center justify-between gap-8">
        <div>
          <p className="type-label-xs text-ink-muted">PRODUCT GATES</p>
          <p className="type-body-sm text-ink-muted mt-3">
            One intake per medication for each stage. Saved independently of
            Healthie form content.
          </p>
        </div>
        {!editing && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setEditing(true)}
          >
            <Pencil size={11} /> Edit links
          </Button>
        )}
      </div>

      {!editing ? (
        <div className="flex flex-wrap gap-6">
          {form.linked_products.map((product) => (
            <span
              key={product.product_id}
              title={product.product_id}
              className="inline-flex items-center gap-4 border border-line bg-sunken px-7 py-4 type-label-xs"
            >
              <Check size={10} aria-hidden /> {product.label}
              <span className="text-ink-muted">
                · {product.usages.join(" + ")}
              </span>
            </span>
          ))}
          {!form.linked_products.length && (
            <span className="type-body-sm text-ink-muted">
              Unassigned draft — it is not currently a medication gate.
            </span>
          )}
        </div>
      ) : (
        <div className="border border-line-subtle">
          <div className="p-8 border-b border-line-subtle">
            <label className="relative block">
              <Search
                size={13}
                className="absolute left-9 top-1/2 -translate-y-1/2 text-ink-muted"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search treatments or medication IDs"
                className="w-full min-h-34 pl-30 pr-9 border border-line bg-raised type-body-sm outline-none focus:shadow-focus"
              />
            </label>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_10rem_10rem] px-9 py-6 border-b border-line-subtle bg-sunken type-label-xs text-ink-muted">
            <span>TREATMENT / MEDICATION ID</span>
            <span>INITIAL</span>
            <span>RENEWAL</span>
          </div>
          <div className="max-h-[28rem] overflow-y-auto">
            {visible.map((product) => (
              <div
                key={product.product_id}
                className="grid grid-cols-[minmax(0,1fr)_10rem_10rem] items-center px-9 py-7 border-b border-line-subtle last:border-b-0"
              >
                <div className="min-w-0 pr-8">
                  <p className="type-body-sm truncate">{product.label}</p>
                  <p className="type-label-xs text-ink-muted truncate">
                    {product.product_id}
                  </p>
                </div>
                {(["Initial", "Renewal"] as IntakeUsage[]).map((usage) => {
                  const owner = owners.get(`${product.product_id}:${usage}`);
                  const checked = Boolean(
                    selection[product.product_id]?.[usage],
                  );
                  return (
                    <label
                      key={usage}
                      title={
                        owner ? `Already used by ${owner.name}` : undefined
                      }
                      className={`type-label-xs inline-flex items-center gap-5 ${owner ? "text-ink-muted" : "text-ink"}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={Boolean(owner)}
                        onChange={(event) =>
                          setSelection((current) => ({
                            ...current,
                            [product.product_id]: {
                              Initial:
                                current[product.product_id]?.Initial ?? false,
                              Renewal:
                                current[product.product_id]?.Renewal ?? false,
                              [usage]: event.target.checked,
                            },
                          }))
                        }
                      />
                      {owner ? "In use" : checked ? "Linked" : "—"}
                    </label>
                  );
                })}
              </div>
            ))}
            {!visible.length && (
              <p className="p-10 type-body-sm text-ink-muted">
                No matching treatments.
              </p>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="flex items-center justify-end gap-7">
          <Button type="button" size="sm" variant="ghost" onClick={cancel}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={update.isPending}
            onClick={save}
          >
            <Save size={12} /> Save product gates
          </Button>
        </div>
      )}
      {message && (
        <p
          className={`type-body-sm ${message.includes("saved") ? "text-success" : "text-danger"}`}
        >
          {message}
        </p>
      )}
    </div>
  );
}

function ModuleEditor({
  formId,
  module,
  index,
  canPublish,
}: {
  formId: string;
  module: IntakeTemplateModule;
  index: number;
  canPublish: boolean;
}) {
  const update = useUpdateClinicianIntakeModule();
  const updateHighlight = useUpdateClinicianIntakeHighlight();
  const [label, setLabel] = useState(module.label);
  const [sublabel, setSublabel] = useState(module.sublabel);
  const [options, setOptions] = useState(module.options);
  const [modType, setModType] = useState(module.mod_type);
  const [required, setRequired] = useState(module.required);
  const [message, setMessage] = useState<string | null>(null);
  const [importance, setImportance] = useState(module.highlight_importance);
  const [highlightLabel, setHighlightLabel] = useState(
    module.highlight_label ?? "",
  );
  const [highlightMessage, setHighlightMessage] = useState<string | null>(null);
  const [highlightEditing, setHighlightEditing] = useState(false);

  const save = async () => {
    if (
      !window.confirm(
        "Publish this question change to Healthie? It changes the intake patients receive.",
      )
    )
      return;
    setMessage(null);
    try {
      await update.mutateAsync({
        formId,
        moduleId: module.id,
        label: label.trim(),
        sublabel: sublabel.trim(),
        options: options.trim(),
        mod_type: modType,
        required,
        index,
        confirmed: true,
      });
      setMessage("Published to Healthie");
    } catch {
      setMessage("Could not publish. Nothing else was changed.");
    }
  };

  const saveHighlight = async () => {
    setHighlightMessage(null);
    try {
      await updateHighlight.mutateAsync({
        formId,
        moduleId: module.id,
        importance,
        label: highlightLabel,
      });
      setHighlightMessage("Highlight saved");
      setHighlightEditing(false);
    } catch {
      setHighlightMessage("Could not save highlight");
    }
  };

  return (
    <article className="border border-line-subtle p-10 grid md:grid-cols-[5rem_minmax(0,1fr)] gap-9">
      <div className="type-label-xs text-ink-muted pt-7">Q{index + 1}</div>
      <div className="flex flex-col gap-7 min-w-0">
        <div className="flex flex-wrap items-center gap-7">
          {canPublish ? (
            <select
              value={modType}
              onChange={(event) => setModType(event.target.value)}
              className="min-h-30 border border-line bg-raised px-7 type-label-xs"
            >
              {!QUESTION_TYPES.some((item) => item.value === modType) && (
                <option value={modType}>{modType}</option>
              )}
              {QUESTION_TYPES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="type-label-xs px-6 py-3 bg-sunken border border-line-subtle">
              {module.mod_type || "text"}
            </span>
          )}
          <label className="type-label-xs inline-flex items-center gap-5">
            <input
              type="checkbox"
              checked={required}
              disabled={!canPublish}
              onChange={(event) => setRequired(event.target.checked)}
            />
            Required
          </label>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-6 min-w-0">
            {importance === "none" ? (
              <span className="type-label-xs text-ink-muted">
                No clinician highlight
              </span>
            ) : (
              <span
                className={`inline-flex items-center gap-4 max-w-[34rem] border px-7 py-4 type-label-xs ${importance === "safety" ? "border-danger/30 bg-danger-wash text-danger" : "border-line bg-accent-wash text-ink"}`}
                title={highlightLabel || importance}
              >
                <span>{importance}</span>
                {highlightLabel && (
                  <span className="normal-case font-normal truncate">
                    · {highlightLabel}
                  </span>
                )}
              </span>
            )}
            {highlightMessage && !highlightEditing && (
              <span
                className={`text-11 ${highlightMessage === "Highlight saved" ? "text-success" : "text-danger"}`}
              >
                {highlightMessage}
              </span>
            )}
            {!highlightEditing && (
              <button
                type="button"
                onClick={() => {
                  setHighlightMessage(null);
                  setHighlightEditing(true);
                }}
                className="inline-flex items-center gap-4 type-label-xs text-ink-muted hover:text-ink"
              >
                <Pencil size={11} aria-hidden /> Edit highlight
              </button>
            )}
          </div>
        </div>
        {highlightEditing && (
          <div className="grid md:grid-cols-[14rem_minmax(0,1fr)_auto] gap-7 items-end bg-sunken p-8 border border-line-subtle">
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              CLINICIAN HIGHLIGHT
              <select
                value={importance}
                onChange={(event) =>
                  setImportance(
                    event.target.value as "none" | "clinical" | "safety",
                  )
                }
                className="min-h-34 border border-line bg-raised px-7 type-body-sm text-ink"
              >
                <option value="none">Do not highlight</option>
                <option value="clinical">Clinical</option>
                <option value="safety">Safety</option>
              </select>
            </label>
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              SHORT LABEL (OPTIONAL)
              <input
                value={highlightLabel}
                onChange={(event) => setHighlightLabel(event.target.value)}
                placeholder={module.label || "Label shown in patient brief"}
                className="min-h-34 border border-line bg-raised px-8 type-body-sm text-ink outline-none focus:shadow-focus"
              />
            </label>
            <div className="flex items-center justify-end gap-5">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setImportance(module.highlight_importance);
                  setHighlightLabel(module.highlight_label ?? "");
                  setHighlightMessage(null);
                  setHighlightEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                loading={updateHighlight.isPending}
                onClick={saveHighlight}
              >
                <Save size={12} /> Save tag
              </Button>
            </div>
            {highlightMessage && (
              <span className="md:col-span-3 text-11 text-danger">
                {highlightMessage}
              </span>
            )}
          </div>
        )}
        <input
          value={label}
          disabled={!canPublish}
          onChange={(event) => setLabel(event.target.value)}
          aria-label={`Question ${index + 1}`}
          className="w-full border border-line bg-sunken px-9 py-7 type-body-sm font-medium outline-none focus:shadow-focus disabled:bg-transparent disabled:border-transparent disabled:px-0"
        />
        {(sublabel || canPublish) && (
          <textarea
            value={sublabel}
            disabled={!canPublish}
            onChange={(event) => setSublabel(event.target.value)}
            rows={2}
            placeholder="Optional helper text"
            className="w-full border border-line bg-sunken px-9 py-7 type-body-sm outline-none resize-y focus:shadow-focus disabled:bg-transparent disabled:border-transparent disabled:px-0"
          />
        )}
        {(options || canPublish) && (
          <textarea
            value={options}
            disabled={!canPublish}
            onChange={(event) => setOptions(event.target.value)}
            rows={3}
            placeholder="One Healthie option per line"
            className="w-full border border-line bg-sunken px-9 py-7 type-data-sm outline-none resize-y focus:shadow-focus disabled:bg-transparent disabled:border-transparent disabled:px-0"
          />
        )}
        {canPublish && (
          <div className="flex items-center justify-between gap-8">
            <span
              className={`text-11 ${message?.startsWith("Published") ? "text-success" : "text-danger"}`}
            >
              {message}
            </span>
            <Button
              type="button"
              size="sm"
              loading={update.isPending}
              disabled={!label.trim()}
              onClick={save}
            >
              <Save size={12} /> Publish question
            </Button>
          </div>
        )}
      </div>
    </article>
  );
}

function NewModuleForm({ formId, index }: { formId: string; index: number }) {
  const create = useCreateClinicianIntakeModule();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [sublabel, setSublabel] = useState("");
  const [modType, setModType] = useState("text");
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="border border-dashed border-line px-10 py-12 type-label-sm text-ink-muted hover:text-ink hover:bg-sunken flex items-center justify-center gap-6"
      >
        <Plus size={13} /> Add Healthie question
      </button>
    );
  }

  const submit = async () => {
    if (
      !window.confirm(
        "Add this question to the live Healthie intake patients receive?",
      )
    )
      return;
    setError(null);
    try {
      await create.mutateAsync({
        formId,
        label: label.trim(),
        sublabel: sublabel.trim(),
        mod_type: modType,
        required,
        options: options.trim(),
        index,
        confirmed: true,
      });
      setOpen(false);
    } catch {
      setError("Could not add the question. Nothing else was changed.");
    }
  };

  return (
    <section className="border border-ink p-11 flex flex-col gap-8">
      <div className="flex items-center justify-between gap-8">
        <h3 className="type-label-sm font-medium">New Healthie question</h3>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="type-label-xs text-ink-muted"
        >
          Cancel
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Question"
          className="min-h-38 border border-line px-9 type-body-sm outline-none focus:shadow-focus"
        />
        <select
          value={modType}
          onChange={(event) => setModType(event.target.value)}
          className="min-h-38 border border-line bg-raised px-9 type-body-sm"
        >
          {QUESTION_TYPES.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>
      <input
        value={sublabel}
        onChange={(event) => setSublabel(event.target.value)}
        placeholder="Helper text (optional)"
        className="min-h-38 border border-line px-9 type-body-sm outline-none focus:shadow-focus"
      />
      {(modType === "radio" || modType === "checkbox") && (
        <textarea
          value={options}
          onChange={(event) => setOptions(event.target.value)}
          rows={3}
          placeholder="One option per line"
          className="border border-line px-9 py-7 type-body-sm outline-none resize-y focus:shadow-focus"
        />
      )}
      <label className="type-label-xs inline-flex items-center gap-5">
        <input
          type="checkbox"
          checked={required}
          onChange={(event) => setRequired(event.target.checked)}
        />
        Required
      </label>
      <div className="flex items-center justify-between gap-8">
        <span className="text-11 text-danger">{error}</span>
        <Button
          type="button"
          size="sm"
          loading={create.isPending}
          disabled={!label.trim()}
          onClick={submit}
        >
          <Plus size={12} /> Publish question
        </Button>
      </div>
    </section>
  );
}

function NewIntakeDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateClinicianIntakeForm();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) return;
    if (
      !window.confirm(
        "Create this blank intake form in Healthie? It starts unassigned and will not affect any medication gate.",
      )
    )
      return;
    setError(null);
    try {
      await create.mutateAsync({ name: name.trim() });
      setName("");
      onClose();
    } catch {
      setError("The intake could not be created. Nothing else was changed.");
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Create intake form"
      footer={
        <div className="border-t border-line-subtle bg-raised p-10">
          <Button
            type="button"
            size="sm"
            className="w-full justify-center"
            loading={create.isPending}
            disabled={!name.trim()}
            onClick={submit}
          >
            <Plus size={12} /> Create blank intake
          </Button>
        </div>
      }
    >
      <div className="p-14 flex flex-col gap-12">
        <div className="flex flex-col gap-5">
          <h2 className="type-heading-md">
            Start with the patient-facing form
          </h2>
          <p className="type-body-sm text-ink-muted">
            This creates a blank Healthie form, then opens it in this editor so
            questions, options, required fields, and clinician highlights can be
            built together. Staging automatically marks it as a staging test
            form and keeps it out of medication eligibility gates.
          </p>
        </div>
        <label className="flex flex-col gap-5 type-label-xs text-ink-muted">
          FORM NAME
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Peptide treatment async review"
            className="min-h-42 border border-line bg-raised px-10 type-body-md text-ink outline-none focus:shadow-focus"
          />
        </label>
        {error && (
          <p role="alert" className="type-body-sm text-danger">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}
